import React, { useMemo } from 'react';
import { Box, Paper, Typography, Stack, Chip, Button, Divider, alpha } from '@mui/material';
import { Download, InsertDriveFile, PictureAsPdf, Description, Slideshow, Image as ImageIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { IParticipantEventFile } from '../../../interfaces/IParticipantEventFile';

interface ParticipantFilesPanelProps {
    files: IParticipantEventFile[];
    onDownload: (file: IParticipantEventFile) => void;
}

const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const getTypeIcon = (type: string) => {
    const ext = type.toLowerCase();
    if (ext === 'pdf') return <PictureAsPdf sx={{ color: '#d32f2f' }} />;
    if (ext === 'doc' || ext === 'docx') return <Description sx={{ color: '#1976d2' }} />;
    if (ext === 'ppt' || ext === 'pptx') return <Slideshow sx={{ color: '#ed6c02' }} />;
    if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'gif') return <ImageIcon sx={{ color: '#2e7d32' }} />;
    return <InsertDriveFile sx={{ color: '#616161' }} />;
};

const parseServerDateToLocal = (value: string) => {
    const normalised = value.replace(' ', 'T');
    const hasOffset = /([zZ]|[+-]\d{2}:?\d{2})$/.test(normalised);
    return new Date(hasOffset ? normalised : `${normalised}Z`);
};

const formatSharedAt = (value: string) => format(parseServerDateToLocal(value), 'MMM d, yyyy h:mm a');

export default function ParticipantFilesPanel({ files, onDownload }: ParticipantFilesPanelProps) {
    const sortedFiles = useMemo(
        () => [...files].sort((a, b) => parseServerDateToLocal(b.updatedAt ?? b.uploadedAt).getTime() - parseServerDateToLocal(a.updatedAt ?? a.uploadedAt).getTime()),
        [files]
    );

    if (sortedFiles.length === 0) {
        return null;
    }

    return (
        <Paper
            elevation={3}
            sx={{
                p: 3,
                borderRadius: 3,
                mb: 4,
                border: `1px solid ${alpha('#493193', 0.1)}`,
                boxShadow: `0 8px 32px ${alpha('#493193', 0.08)}`,
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #493193 0%, #2fadd8 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    Shared Files
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Files shared with you for this event.
                </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={1.5}>
                {sortedFiles.map((file) => (
                    <Box
                        key={file.id}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: alpha('#493193', 0.03),
                            border: `1px solid ${alpha('#493193', 0.12)}`,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                            {getTypeIcon(file.type)}
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }} noWrap>
                                    {file.name}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                    <Chip size="small" label={file.type.toUpperCase()} variant="outlined" />
                                    <Typography variant="caption" color="text.secondary">
                                        {formatFileSize(file.size)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Shared {formatSharedAt(file.updatedAt ?? file.uploadedAt)}
                                    </Typography>
                                </Stack>
                            </Box>
                        </Stack>

                        <Button
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={() => onDownload(file)}
                            sx={{
                                borderRadius: '50px',
                                borderColor: alpha('#493193', 0.3),
                                color: '#493193',
                                fontWeight: 600,
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: '#493193',
                                    bgcolor: alpha('#493193', 0.05),
                                },
                            }}
                        >
                            Download
                        </Button>
                    </Box>
                ))}
            </Stack>
        </Paper>
    );
}
